/**
 * Fait : la boîte de COMPARAISON (critère 29) — choisir deux ou trois
 *   modèles, voir le NOMBRE D'APPELS et l'ESTIMATION HAUTE de chacun AVANT
 *   de confirmer, puis lancer. L'abonnement n'affiche aucun montant : son
 *   quota ne s'additionne pas aux euros (D-015). Au-delà de trois, la case
 *   est simplement désactivée — l'erreur est empêchée, pas réparée.
 *
 * Ne fait pas : elle ne lance rien d'elle-même — le parent reçoit la
 *   confirmation et lance — et ne calcule aucun montant : l'API estime,
 *   l'écran affiche.
 */

import { useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { estimeLaComparaison, type EstimationVue, type ModeleVu } from './api-fil.ts';

/** Trois modèles au plus — la borne du critère 29, affichée et tenue. */
const MODELES_MAXIMUM = 3;

/** Affiche des centimes en euros lisibles — l'unité reste le centime. */
function enEuros(centimes: number): string {
  return `${(centimes / 100).toFixed(2).replace('.', ',')} €`;
}

export function Comparateur({
  filId,
  brancheId,
  texte,
  modeles,
  surConfirmation,
  surFermeture,
}: {
  readonly filId: string;
  readonly brancheId: string;
  readonly texte: string;
  readonly modeles: readonly ModeleVu[];
  surConfirmation: (entreesIds: string[]) => void;
  surFermeture: () => void;
}) {
  const [choisis, setChoisis] = useState<string[]>([]);
  const [estimation, setEstimation] = useState<EstimationVue | null>(null);
  const [echec, setEchec] = useState<string | null>(null);

  function bascule(id: string): void {
    setEstimation(null);
    setChoisis((avant) =>
      avant.includes(id) ? avant.filter((choisi) => choisi !== id) : [...avant, id],
    );
  }

  /** Demande l'estimation — l'étape OBLIGÉE avant la confirmation. */
  async function estime(): Promise<void> {
    const issue = await estimeLaComparaison(filId, brancheId, choisis, texte);
    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    setEchec(null);
    setEstimation(issue.valeur);
  }

  return (
    <section data-role="comparateur" aria-label="Comparer des modèles">
      <h3>Comparer jusqu’à trois modèles</h3>

      {/*
       * Des JETONS cochables, et non une liste à puces : à fort grossissement,
       * une puce noire suivie d'une petite case est une cible minuscule dans
       * un alignement mou. Un bloc bordé se vise.
       */}
      <ul className="jetons">
        {modeles.map((modele) => (
          <li key={modele.id}>
            <label className="jeton">
              <input
                type="checkbox"
                checked={choisis.includes(modele.id)}
                disabled={!choisis.includes(modele.id) && choisis.length >= MODELES_MAXIMUM}
                onChange={() => {
                  bascule(modele.id);
                }}
              />{' '}
              {modele.libelle}
            </label>
          </li>
        ))}
      </ul>

      {echec !== null && <Annonce urgence="urgent">{echec}</Annonce>}

      {estimation === null ? (
        <button type="button" disabled={choisis.length < 2} onClick={() => void estime()}>
          Estimer le coût
        </button>
      ) : (
        <div data-role="estimation">
          <p>
            <strong>{estimation.nombreDAppels}</strong> appel(s) partiront — un par modèle.
          </p>
          <ul className="rangees">
            {estimation.parModele.map((modele) => (
              <li key={modele.entreeDeCatalogueId}>
                {modele.libelle} :{' '}
                {modele.voie === 'cle_api' && modele.estimationHauteCentimes !== undefined
                  ? `au plus ${enEuros(modele.estimationHauteCentimes)}`
                  : 'sur l’abonnement — compte contre son quota, pas en euros'}
              </li>
            ))}
          </ul>
          <button
            type="button"
            data-role="confirmer-comparaison"
            onClick={() => {
              surConfirmation(choisis);
            }}
          >
            Confirmer et comparer
          </button>
        </div>
      )}

      <button type="button" onClick={surFermeture}>
        Renoncer
      </button>
    </section>
  );
}
